import { Injectable } from '@angular/core';
import { AuthenticationResult, AuthError, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';
import { AuthData, LoginData, SignupData } from '../store';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { BrowserStorageService, BrowserStorageType } from './browser-storage.service';
import {
  AzureLogin,
  CreateUser,
  GetUserFromSession,
  Login,
  SetNotification,
  UpdateUser,
} from '../store/api-eco.actions';
import { Store } from '@ngxs/store';
import { environment } from 'src/environments/environment';
import { ProgressSpinnerDialogService } from './progress-spinner-dialog.service';
import { APIELogService } from './apie-log.service';
import { AlertMessages } from 'src/assets/data/alert-messages';

export const SESSION_CONTEXT_ID = 'session_context';
export const GLOBAL_CONTEXT_ID = 'global_context';

/**
 *
 */
interface IdTokenClaims extends AuthenticationResult {
  idTokenClaims: any;
}

/**
 *
 */
export enum ContextStatusType {
  LOGIN_IN_PROGRESS = 'loginInProgress',
  LOGGED_IN = 'loggedIn',
  LOGGED_IN_PENDING_REFRESH = 'loggedInPendingRefresh',
  UNAUTHORIZED = 'validated',
  AUTH_ERROR = 'authError',
  LOGGED_OUT = 'loggedOut',
  INITIALIZED = 'initialized',
  EMPTY = 'empty',
}

/**
 *
 */
export enum ContextUpdateType {
  STATUS = 'status',
  AUTH_DATA = 'authData',
  REFRESH = 'refresh',
  TOKEN = 'token',
  TOKEN_EXPIRY = 'tokenExpiry',
  USER_CONTEXT = 'userContext',
}

/**
 *
 */
export enum ContextActionType {
  PREINIT = 'preInit',
  INIT = 'init',
  UPDATE = 'validated',
  LOGOUT = 'loggedOut',
  LOGOUT_AND_CLEAR_STORAGE = 'loggedOut',
}

/**
 *
 */
export interface TokenInfo {
  token: string;
  tokenExpiry: any;
  authData: AuthData;
}

/**
 *
 */
export interface CommonContext extends TokenInfo {
  status: ContextStatusType;
  isPersistent: boolean;
  lastReload: string;
  lastReloadInTicks: number;
}

export type StorageContext = CommonContext;

export type SessionContext = StorageContext;

export type GlobalContext = StorageContext;

/**
 *
 */
export interface UserContext {
  session: SessionContext;
  global: GlobalContext;
}

/**
 *
 */
@Injectable({
  providedIn: 'root',
})
export class UserContextService {
  private _userContext: UserContext = {
    session: {
      isPersistent: false,
      token: undefined,
      tokenExpiry: undefined,
      authData: undefined,
      status: ContextStatusType.EMPTY,
    } as SessionContext,
    global: {
      isPersistent: false,
      token: undefined,
      tokenExpiry: undefined,
      authData: undefined,
      status: ContextStatusType.EMPTY,
    } as GlobalContext,
  } as UserContext;

  private _b2cPolicies: any;
  private _defaultSessionContext: SessionContext;
  private _defaultGlobalContext: GlobalContext;
  private _defaultCommonContext: CommonContext;
  private _destroyed: Subject<unknown>;
  private _logPrefix: string = environment.userContext.logMessagePrefix;

  private readonly _sessionContextId = `apie.${environment.environmentName}.${SESSION_CONTEXT_ID}`.toLowerCase();
  private readonly _globalContextId = `apie.${environment.environmentName}.${GLOBAL_CONTEXT_ID}`.toLowerCase();

  /**
   * Enables verbose logging; can be set from environment variables.
   */
  public verbose: boolean = environment.userContext.logging.userContextService;

  public userContext$ = new BehaviorSubject(this._userContext);

  /**
   *
   * @param store
   * @param browserStorageService
   * @param msalBroadcastService
   * @param authService
   * @param progressDialogService
   * @param logService
   */
  constructor(
    private store: Store,
    private browserStorageService: BrowserStorageService,
    private msalBroadcastService: MsalBroadcastService,
    private authService: MsalService,
    private progressDialogService: ProgressSpinnerDialogService,
    private logService: APIELogService
  ) {
    // Set B2C policies
    this._b2cPolicies = environment.b2cPolicies;

    const defaultStorageContext = {
      isPersistent: false,
      status: ContextStatusType.EMPTY,
      token: undefined,
      tokenExpiry: undefined,
      authData: undefined,
    } as StorageContext;

    this._defaultCommonContext = {
      status: ContextStatusType.EMPTY,
      token: undefined,
      tokenExpiry: undefined,
      authData: undefined,
    } as CommonContext;

    // Default context(s) state
    this._defaultSessionContext = defaultStorageContext as SessionContext;
    this._defaultGlobalContext = defaultStorageContext as GlobalContext;
  }

  /**
   *
   * @param destroyed
   */
  public initService(destroyed: Subject<unknown>) {
    this.logService.log(`Initializing User Context Service...`, 'info', `${this._logPrefix} -> INIT`);

    this._destroyed = destroyed;

    this.initUserContext();

    // Subscriptions
    this.subscribeToUserDetailFlow();
    this.subscribeToResetPasswordFlow();
    this.subscribeToAuthFailure();
    this.subscribeToLoginFlow();
  }

  /**
   *
   */
  private initUserContext() {
    this.logService.log(`Initializing STORAGE Contexts`, 'info', `${this._logPrefix} -> INIT`);
    this.initGlobalStorageContext();

    // Update subscribers
    this.userContext$.next(this.getContext());
  }

  /**
   *
   */
  private initGlobalStorageContext(): void {
    this.logService.log(
      `Initializing GLOBAL STORAGE Context:  ${this._globalContextId}`,
      'info',
      `${this._logPrefix} -> INIT`
    );

    // get global context from localStorage
    this._userContext.global = this.browserStorageService.getItem(BrowserStorageType.LOCAL, this._globalContextId);

    if (!this._userContext.global || !this._userContext.global.isPersistent) {
      this._userContext.global = this._defaultGlobalContext;
      this._userContext.global.isPersistent = true;
      this._userContext.global.status = ContextStatusType.INITIALIZED;
      this.updateStorageContext(BrowserStorageType.LOCAL, this._userContext.global);
    }

    // Mirror global to session (in-memory only)
    this._userContext.session = { ...this._userContext.global };
  }

  /**
   * Update instance context from browser storage. If context is not available, a non persisent context will be assigned.
   * @returns
   */
  public getTokenInfo(): TokenInfo {
    const userContext = this.getContext();

    return {
      token: userContext.global.token,
      tokenExpiry: userContext.global.tokenExpiry,
      authData: userContext.global.authData,
    } as TokenInfo;
  }

  /**
   * Update instance context from browser storage. If context is not available, a non persisent context will be assigned.
   * @returns
   */
  public getContext(): UserContext {
    // Read from localStorage only
    this._userContext.global = this.browserStorageService.getItem(BrowserStorageType.LOCAL, this._globalContextId);

    if (!this._userContext.global) {
      this._userContext.global = this._defaultGlobalContext;
    }

    // Mirror global to session (in-memory)
    this._userContext.session = { ...this._userContext.global };

    this.userContext$.next(this._userContext);

    return this._userContext;
  }

  /**
   *
   * @param property
   * @param value
   * @param type
   * @param withRefreshFromStorage
   */
  public updateContext(
    property: ContextUpdateType,
    value: ContextStatusType | UserContext | AuthData | string,
    _type: BrowserStorageType = BrowserStorageType.LOCAL,
    withRefreshFromStorage: boolean = true
  ): void {
    if (withRefreshFromStorage) this.refreshContext();
    let isPersistent = true;

    switch (property) {
      case ContextUpdateType.TOKEN:
        this._userContext.global.token = value as string;
        break;
      case ContextUpdateType.TOKEN_EXPIRY:
        this._userContext.global.tokenExpiry = value as string;
        break;
      case ContextUpdateType.STATUS:
        this._userContext.global.status = value as ContextStatusType;
        break;
      case ContextUpdateType.REFRESH:
        this._userContext.global.status = ContextStatusType.LOGGED_IN_PENDING_REFRESH;
        break;
      case ContextUpdateType.AUTH_DATA:
        this._userContext.global.authData = value as AuthData;
        break;
      case ContextUpdateType.USER_CONTEXT:
        this._userContext.global = (value as UserContext).global;
        break;
      default:
        isPersistent = false;
        break;
    }

    if (isPersistent) {
      this._userContext.global.isPersistent = isPersistent;
    }

    // Always write to localStorage
    this.updateStorageContext(BrowserStorageType.LOCAL, this._userContext.global);

    // Mirror to in-memory session
    this._userContext.session = { ...this._userContext.global };

    this.userContext$.next(this._userContext);
  }

  /**
   *
   */
  public logout() {
    this.resetContext('Logout out of session');
    this.clearAllBrowserStorage();
  }

  public clearAllBrowserStorage(): void {
    this.browserStorageService.clear(BrowserStorageType.ALL);
  }

  /**
   *
   */
  public preInitContext() {
    this.resetContext('Initializing session');
  }

  /**
   *
   */
  public resetContextAfterLogin() {
    this.logService.log(`Login...`, 'info', `${this._logPrefix} -> LOGIN`);
    this._userContext.session.isPersistent = true;
    this._userContext.global.isPersistent = true;
  }

  /**
   *
   */
  private refreshContext(): void {
    this.getContext();
  }

  /**
   *
   * @param type
   * @param context
   */
  private updateStorageContext(_type: BrowserStorageType, context: StorageContext): void {
    // Always write to localStorage only
    this.browserStorageService.setItem(BrowserStorageType.LOCAL, this._globalContextId, context);
  }

  /**
   *
   */
  private resetUserContext(_clearStorage?: boolean) {
    this.logService.log(`Resetting user context for session and local storage`, 'info', `${this._logPrefix} -> RESET`);

    this.browserStorageService.removeItem(BrowserStorageType.LOCAL, this._globalContextId);

    // Also clear NGXS storage plugin keys
    localStorage.removeItem('userModel');
    localStorage.removeItem('route');

    this._userContext = {
      session: this._defaultSessionContext,
      global: this._defaultGlobalContext,
    } as UserContext;
  }

  /**
   *
   */
  private subscribeToUserDetailFlow() {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroyed)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();

        const currentAcount = this.authService.instance.getActiveAccount();
        const tokenInfo = this.getTokenInfo();
        if (currentAcount) {
          this.logService.log(
            `Retreived MSAL context from app memory (session or local)...`,
            'success',
            `${this._logPrefix} -> LOGGED_IN 🍺  `
          );

          this.updateContext(ContextUpdateType.STATUS, ContextStatusType.LOGGED_IN, BrowserStorageType.ALL);

          // Only fetch user from session if they are not already fully authenticated.
          // This prevents GetUserFromSession firing on every silent token acquisition.
          if (!tokenInfo?.token) {
            this.store.dispatch(new GetUserFromSession(currentAcount));
          } else {
            this.logService.log(
              'User already authenticated with token — skipping GetUserFromSession.',
              'info',
              `${this._logPrefix} -> ALREADY_LOGGED_IN`
            );
          }
        }
      });
  }

  /**
   *
   */
  private subscribeToResetPasswordFlow() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || msg.eventType === EventType.LOGIN_SUCCESS
        ),
        takeUntil(this._destroyed)
      )
      .subscribe((result: EventMessage) => {
        // If returning from successful password reset request, force logout to complete process
        const payload: IdTokenClaims = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
        this.logService.log(`Return from Password Reset...`, 'info');
        if (payload.idTokenClaims?.tfp === this._b2cPolicies.names.resetPassword) {
          this.store.dispatch(
            new SetNotification({
              error: false,
              warning: false,
              message: AlertMessages.SuccessResetPasswordFromLogin,
              title: 'Success',
              duration: 5000,
            })
          );
          this.logService.log(`LOGOUT of Auth Service.`, 'warning');
          setTimeout(() => {
            this.authService.logout();
          }, 5000);
        }
        return result;
      });
  }

  /**
   *
   */
  private subscribeToAuthFailure() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntil(this._destroyed)
      )
      .subscribe((result: EventMessage) => {
        if (result.error instanceof AuthError) {
          // Check for forgot password error
          // Learn more about AAD error codes at https://docs.microsoft.com/azure/active-directory/develop/reference-aadsts-error-codes
          const userContext = this.getContext();

          if (userContext.global.status === ContextStatusType.LOGIN_IN_PROGRESS) {
            this.progressDialogService.showSpinner();
          }

          this.updateContext(ContextUpdateType.STATUS, ContextStatusType.UNAUTHORIZED, BrowserStorageType.ALL);

          this.logService.log(
            `Auth error message:\n${result.error.message}`,
            'error',
            `${this._logPrefix} -> AUTH ERROR 🚨 🚨 🚨`
          );

          if (result.error.message.includes('AADB2C90118')) {
            // login request with reset authority
            const resetPasswordFlowRequest = {
              scopes: ['openid'],
              authority: this._b2cPolicies.authorities.resetPassword.authority,
            };
            this.store.dispatch(new AzureLogin(resetPasswordFlowRequest));
          }
          if (result.error.message.includes('AADB2C90091')) {
            // login request with reset authority
            const resetPasswordFlowRequest = {
              scopes: ['openid'],
              authority: this._b2cPolicies.authorities.signIn.authority,
            };

            this.logService.log(
              `Azure: Reset Password Flow (Request)`,
              'success',
              `${this._logPrefix} -> AZ RESET 🍺 `
            );
            this.store.dispatch(new AzureLogin(resetPasswordFlowRequest));
          }
        }
      });
  }

  /**
   *
   */
  private subscribeToLoginFlow() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this._destroyed)
      )
      .subscribe({
        next: (result: EventMessage) => {
          const userContext = this.getContext();

          // Display spinner if logging in.
          if (userContext.global.status === ContextStatusType.LOGIN_IN_PROGRESS) {
            this.progressDialogService.showSpinner();
          }

          const payload: IdTokenClaims = result.payload as AuthenticationResult;

          this.authService.instance.setActiveAccount(payload.account);
          const loginData: LoginData = {
            email: payload.idTokenClaims?.username, //emails[0],
            homeAccountId: payload.account.homeAccountId,
            idToken: payload.idToken,
          };
          // catching edit profile
          if (payload.idTokenClaims?.acr === this._b2cPolicies.names.editProfile) {
            const updateUserData = {
              firstName: payload.idTokenClaims?.given_name,
              lastName: payload.idTokenClaims?.family_name,
              displayName: payload.idTokenClaims?.name, //`${payload.idTokenClaims?.given_name} ${payload.idTokenClaims?.family_name}`,
              email: payload.idTokenClaims?.username, //emails[0],
              phoneNumber: payload.idTokenClaims?.strongAuthenticationPhoneNumber,
              organizationName: payload.idTokenClaims?.extension_business,
              country: payload.idTokenClaims?.country,
              homeAccountId: payload.account.homeAccountId,
              idToken: payload.idToken,
            };
            this.store.dispatch(new UpdateUser(updateUserData));
          }
          if (payload.idTokenClaims?.isForgotPassword === true) {
            this.store.dispatch(
              new SetNotification({
                error: false,
                warning: false,
                message: AlertMessages.SuccessResetPassword,
                title: 'Success',
                duration: 5000,
              })
            );
          }
          if (payload.idTokenClaims?.acr === this._b2cPolicies.names.updatePhoneNumber) {
            const updatePhoneData = {
              phoneNumber: payload.idTokenClaims?.strongAuthenticationPhoneNumber,
              email: payload.idTokenClaims?.username, //emails[0],
              homeAccountId: payload.account.homeAccountId,
              idToken: payload.idToken,
              phoneUpdateOnly: true,
            };
            this.store.dispatch(new UpdateUser(updatePhoneData));
          }
          if (payload.idTokenClaims?.newUser === true && result.eventType === EventType.LOGIN_SUCCESS) {
            // make signup request to backend
            const signupData: SignupData = {
              firstName: payload.idTokenClaims?.given_name,
              lastName: payload.idTokenClaims?.family_name,
              displayName: payload.idTokenClaims?.name, //`${payload.idTokenClaims?.given_name} ${payload.idTokenClaims?.family_name}`,
              email: payload.idTokenClaims?.username, //emails[0],
              phoneNumber: payload.idTokenClaims?.strongAuthenticationPhoneNumber,
              organizationName: payload.idTokenClaims?.extension_business,
              country: payload.idTokenClaims?.country,
              homeAccountId: payload.account.homeAccountId,
              idToken: payload.idToken,
            };
            this.store.dispatch(new CreateUser(signupData));
          } else {
            this.resetContextAfterLogin();
            this.updateContext(ContextUpdateType.STATUS, ContextStatusType.LOGGED_IN, BrowserStorageType.ALL);

            this.logService.log(
              'Updating User Context after login success...',
              'success',
              `${this._logPrefix} -> LOGIN 🍺  `
            );

            this.store.dispatch(new Login(loginData));
          }
        },
        error: error => console.log(error),
      });
  }

  /**
   *
   */
  private checkAndSetActiveAccount(): void {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      this.logService.log(`Setting Active Azure Account`, 'info', `${this._logPrefix} -> LOGIN`);
    }
  }

  /**
   *
   * @param message
   */
  private resetContext(message: string) {
    this.logService.log(`${message}...`, 'success', `${this._logPrefix} -> LOGOUT 🍺 `);

    this.resetUserContext();

    // NOTE: Discuss proper logout methodology
    // this.authService.instance.logout(<EndSessionRequest>{})
    this.userContext$.next(this._userContext);
  }
}
